import type { Point } from '@turf/turf';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CoreMap } from 'components/map/core-map';
import { MapboxStyle } from 'components/map/core-map.constants';
import { useMapboxBoundsChange } from 'core/utils/hooks/use-mapbox-bounds';
import { useFeatureLoadReady } from 'core/utils/segment/feature-load-timing.context';
import type { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { FeatureCollection } from 'geojson';
import RegionalRiskChangeMode from '@features/regional-overview/components/regional-risk-change-mode/regional-risk-change-mode.component';
import RegionalRiskReCenterButton from '@features/regional-overview/components/regional-risk-re-center-button/regional-risk-re-center-button';
import { MAP_INITIAL_POSITION } from '@features/regional-overview/regional-overview.models';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import type React from 'react';
import { useCallback, useState } from 'react';
import { AutoSizer } from 'react-virtualized';
import './regional-overview-mycotoxin-map-container.less';
import { useRenderMycotoxinMap } from './use-render-mycotoxin-map';
import { useRegionalOverviewReady } from '../../regional-overview-navigation';

interface RegionalOverviewMycotoxinMapContainerProps {
  filteredPoints: FeatureCollection<Point, RegionalOverviewProperties> | undefined;
  isLoading: boolean;
  onBoundsChanged?: (bounds: mapboxgl.LngLatBounds) => void;
  isNavigationDataReady?: boolean;
}

const RegionalOverviewMycotoxinMapContainer: React.FC<RegionalOverviewMycotoxinMapContainerProps> = ({
  filteredPoints,
  isLoading,
  onBoundsChanged,
  isNavigationDataReady = false
}) => {
  const [mapbox, setMapbox] = useState<mapboxgl.Map>();
  const { hasRendered } = useRenderMycotoxinMap({ map: mapbox, data: filteredPoints });
  useRegionalOverviewReady(isNavigationDataReady && hasRendered);
  useFeatureLoadReady(!isLoading && hasRendered, { regionalModel: 'mycotoxin' });

  useMapboxBoundsChange(mapbox, onBoundsChanged);

  const handleMapbox = useCallback((mapboxProp: mapboxgl.Map) => {
    setMapbox(mapboxProp);
  }, []);

  return (
    <div className='sd-regional-overview-mycotoxin-map' data-testid='regional-overview-mycotoxin'>
      <AutoSizer>
        {() => (
          <CoreMap
            style={MapboxStyle.SATELLITE_STREETS}
            onMapLoaded={handleMapbox}
            minZoom={5}
            maxZoom={19}
            center={MAP_INITIAL_POSITION}
          />
        )}
      </AutoSizer>
      <RegionalRiskChangeMode mapbox={mapbox} mapboxIsLoaded={hasRendered} />
      <RegionalRiskReCenterButton mapbox={mapbox} data={filteredPoints?.features} />

      {isLoading && (
        <div className='sd-regional-risk-loading' data-testid='regional-risk-loading'>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#0071CD' }} spin />} spinning />
        </div>
      )}
    </div>
  );
};

export default RegionalOverviewMycotoxinMapContainer;
